import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loader2, Trash2, Mail, Phone, CheckCircle2, Inbox } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

export default function ContactInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { toast } = useToast();

  const fetch = () => {
    setLoading(true);
    api.get("/contact").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(fetch, []);

  const markRead = async (id) => {
    await api.put(`/contact/${id}/read`);
    fetch();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/contact/${id}`);
    toast({ title: "Deleted" });
    if (selected?.id === id) setSelected(null);
    fetch();
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#0B2A6B]">Contact Inbox</h2>
        <p className="text-sm text-slate-500">All messages submitted via the contact form.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        {/* List */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <Inbox className="h-4 w-4 text-slate-500" />
            <span className="font-semibold text-sm text-slate-700">Messages</span>
            <span className="ml-auto text-xs text-slate-400">{items.length} total</span>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center text-slate-400 text-sm">No messages yet</div>
            ) : items.map((it) => (
              <button
                key={it.id}
                onClick={() => { setSelected(it); if (!it.read) markRead(it.id); }}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-yellow-50 transition flex items-start gap-3 ${
                  selected?.id === it.id ? "bg-yellow-50" : ""
                }`}
              >
                <div className={`h-2 w-2 rounded-full mt-2 ${it.read ? "bg-slate-300" : "bg-yellow-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline">
                    <span className={`text-sm ${it.read ? "font-medium text-slate-700" : "font-bold text-[#0B2A6B]"}`}>{it.name}</span>
                    <span className="text-[11px] text-slate-400 shrink-0">{new Date(it.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{it.subject || "(no subject)"}</div>
                  <div className="text-xs text-slate-400 truncate mt-0.5">{it.message}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          {selected ? (
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-[#0B2A6B]">{selected.subject || "(no subject)"}</h3>
                  <p className="text-sm text-slate-500 mt-1">From <span className="font-semibold text-slate-700">{selected.name}</span></p>
                </div>
                <div className="flex gap-2">
                  {!selected.read && (
                    <button onClick={() => markRead(selected.id)} className="px-3 py-1.5 text-xs rounded-lg bg-yellow-400 text-[#0B2A6B] font-semibold flex items-center gap-1 hover:bg-yellow-500">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Mark Read
                    </button>
                  )}
                  <button onClick={() => remove(selected.id)} className="px-3 py-1.5 text-xs rounded-lg bg-red-50 text-red-600 font-semibold flex items-center gap-1 hover:bg-red-100">
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mb-5 text-sm">
                <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 text-slate-700 hover:text-yellow-600">
                  <Mail className="h-4 w-4" />{selected.email}
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2 text-slate-700 hover:text-yellow-600">
                    <Phone className="h-4 w-4" />{selected.phone}
                  </a>
                )}
              </div>
              <div className="bg-slate-50 rounded-xl p-5 text-slate-700 whitespace-pre-wrap leading-relaxed">{selected.message}</div>
              <div className="text-xs text-slate-400 mt-4">Received {new Date(selected.created_at).toLocaleString()}</div>
            </div>
          ) : (
            <div className="py-20 text-center text-slate-400">
              <Inbox className="h-12 w-12 mx-auto mb-3 opacity-50" />
              Select a message to view
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
